import React, { lazy, Suspense, useEffect, useRef } from 'react';
import { ActivityIndicator, View, StyleSheet, Animated, Platform } from 'react-native';
import { Text } from '../components/AppText';
import { createDrawerNavigator } from '@react-navigation/drawer';
import type { DrawerContentComponentProps } from '@react-navigation/drawer';
import { useNavigation, useIsFocused } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '../theme';
import { PressableScale, useReducedMotion } from '../components/Motion';

const Drawer = createDrawerNavigator();

const ExpensesScreen  = lazy(() => import('../screens/ExpensesScreen').then(m => ({ default: m.ExpensesScreen })));
const AssistantScreen = lazy(() => import('../screens/AssistantScreen').then(m => ({ default: m.AssistantScreen })));
const ReportsScreen   = lazy(() => import('../screens/ReportsScreen').then(m => ({ default: m.ReportsScreen })));

function FadeScreen({ children }: { children: React.ReactNode }) {
  if (Platform.OS === 'web') return <View style={{ flex: 1 }}>{children}</View>;
  return <FadeScreenNative>{children}</FadeScreenNative>;
}

function FadeScreenNative({ children }: { children: React.ReactNode }) {
  const isFocused     = useIsFocused();
  const reducedMotion = useReducedMotion();
  const opacity       = useRef(new Animated.Value(isFocused ? 1 : 0)).current;
  const translateY    = useRef(new Animated.Value(isFocused ? 0 : 6)).current;

  useEffect(() => {
    if (reducedMotion) {
      opacity.setValue(isFocused ? 1 : 0);
      translateY.setValue(isFocused ? 0 : 6);
      return;
    }
    Animated.parallel([
      Animated.timing(opacity,    { toValue: isFocused ? 1 : 0, duration: isFocused ? 220 : 100, useNativeDriver: true }),
      Animated.timing(translateY, { toValue: isFocused ? 0 : 6, duration: isFocused ? 280 : 100, useNativeDriver: true }),
    ]).start();
  }, [isFocused, opacity, reducedMotion, translateY]);

  return (
    <Animated.View style={{ flex: 1, opacity, transform: [{ translateY }] }}>
      {children}
    </Animated.View>
  );
}

function ScreenFallback() {
  return <View style={s.screenFallback}><ActivityIndicator color={Colors.primary} /></View>;
}

function FadedAssistant(props: any)   { return <FadeScreen><Suspense fallback={<ScreenFallback />}><AssistantScreen {...props} /></Suspense></FadeScreen>; }
function FadedCommitments(props: any) { return <FadeScreen><Suspense fallback={<ScreenFallback />}><ExpensesScreen  {...props} /></Suspense></FadeScreen>; }
function FadedReports(props: any)     { return <FadeScreen><Suspense fallback={<ScreenFallback />}><ReportsScreen   {...props} /></Suspense></FadeScreen>; }

const NAV_ITEMS = [
  { key: 'Assistente',  label: 'Assistente',   icon: 'chatbubble-outline' as const,  activeIcon: 'chatbubble'  as const },
  { key: 'Gastos',      label: 'Lançamentos',  icon: 'receipt-outline'    as const,  activeIcon: 'receipt'     as const },
  { key: 'Relatorios',  label: 'Relatórios',   icon: 'bar-chart-outline'  as const,  activeIcon: 'bar-chart'   as const },
] as const;

function DrawerContent({ state, navigation }: DrawerContentComponentProps) {
  const activeKey = state.routes[state.index]?.name;
  const insets    = useSafeAreaInsets();
  return (
    <View style={[
      s.drawerContainer,
      Platform.OS === 'web'
        ? (s.drawerContainerWeb as any)
        : { paddingTop: insets.top, paddingBottom: insets.bottom },
    ]}>
      <View style={s.drawerHeader}>
        <Text style={s.drawerAppTitle}>Casa em Dia</Text>
      </View>
      {NAV_ITEMS.map(item => {
        const isActive = activeKey === item.key;
        return (
          <PressableScale
            key={item.key}
            onPress={() => navigation.navigate(item.key)}
            accessibilityLabel={item.label}
            containerStyle={s.navItemContainer}
            contentStyle={[s.navItem, isActive && s.navItemActive]}
          >
            <Ionicons
              name={isActive ? item.activeIcon : item.icon}
              size={20}
              color={isActive ? Colors.primary : Colors.text2}
            />
            <Text style={[s.navItemLabel, isActive && s.navItemLabelActive]}>
              {item.label}
            </Text>
          </PressableScale>
        );
      })}
    </View>
  );
}

/** Botão ≡ para abrir o drawer — use nos headers das telas */
export function DrawerMenuButton() {
  const navigation = useNavigation<any>();
  return (
    <PressableScale
      onPress={() => navigation.openDrawer()}
      accessibilityLabel="Abrir menu de navegação"
      containerStyle={s.menuBtnContainer}
      contentStyle={s.menuBtnContent}
    >
      <Ionicons name="menu-outline" size={22} color={Colors.text1} />
    </PressableScale>
  );
}

export function TabNavigator() {
  return (
    <Drawer.Navigator
      initialRouteName="Assistente"
      drawerContent={(props) => <DrawerContent {...props} />}
      screenOptions={{
        headerShown: false,
        drawerType: 'front',
        drawerPosition: 'left',
        drawerStyle: {
          backgroundColor: Colors.bg,
          width: 260,
          borderRightWidth: StyleSheet.hairlineWidth,
          borderRightColor: Colors.border,
        } as any,
        overlayColor: 'rgba(0,0,0,0.22)',
        swipeEnabled: Platform.OS !== 'web',
        sceneContainerStyle: { flex: 1, backgroundColor: Colors.bg },
      }}
    >
      <Drawer.Screen name="Assistente"  component={FadedAssistant} />
      <Drawer.Screen name="Gastos"      component={FadedCommitments} />
      <Drawer.Screen name="Relatorios"  component={FadedReports} />
    </Drawer.Navigator>
  );
}

const s = StyleSheet.create({
  screenFallback: {
    flex: 1,
    minHeight: 324,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Colors.bg,
  },
  /* Drawer container */
  drawerContainer: {
    flex: 1,
    backgroundColor: Colors.bg,
  },
  drawerContainerWeb: {
    paddingTop: 'env(safe-area-inset-top, 0px)',
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
  },
  drawerHeader: {
    paddingHorizontal: 18,
    paddingTop: 22,
    paddingBottom: 18,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: Colors.border,
    marginBottom: 7,
  },
  drawerAppTitle: {
    color: Colors.text1,
    fontSize: 20,
    fontWeight: '800',
    letterSpacing: -0.45,
  },
  /* Nav items */
  navItemContainer: {
    borderRadius: 11,
    marginHorizontal: 9,
    marginVertical: 2,
  },
  navItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 13,
    paddingHorizontal: 13,
    paddingVertical: 12,
    borderRadius: 11,
  },
  navItemActive: {
    backgroundColor: Colors.primaryLt,
  },
  navItemLabel: {
    color: Colors.text2,
    fontSize: 14,
    fontWeight: '600',
  },
  navItemLabelActive: {
    color: Colors.primary,
    fontWeight: '700',
  },
  /* ≡ button */
  menuBtnContainer: { borderRadius: 7 },
  menuBtnContent:   { width: 44, height: 44, borderRadius: 11, alignItems: 'center', justifyContent: 'center' },
});
